import React, { useContext, useState } from "react";
import {
  Card,
  Button,
  Checkbox,
  Modal,
  Divider,
  Form,
  Input,
  Row,
  Col,
  Alert,
  Typography,
  Popover,
  Tooltip,
  message,
} from "antd";
import {
  CreditCardOutlined,
  SafetyOutlined,
  DollarOutlined,
  FileTextOutlined,
  ShoppingOutlined,
  CheckOutlined,
  ShoppingCartOutlined,
  QuestionCircleOutlined,
  TruckOutlined,
  EditOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { BASE_URL, API } from "../../../utils/constants";
import { OpenPayContext } from "./OpenPayProvider";
import TermsAndConditionsModal from "./TermsAndConditionsModal";
import DHLShippingCalculator from "./DHLShippingCalculator";
import AddressEditModal from "./AddressEditModal";
import "../../../scss/PurchaseSummaryCard.css";
import securityPng from "../../../images/openpay/security.png";
import radioOnPng from "../../../images/openpay/radio_on.png";
import openpayLogoPng from "../../../images/openpay/openpay.png";
import cvvPng from "../../../images/openpay/cvv.png";
import cards1Png from "../../../images/openpay/cards1.png";
import cards2Png from "../../../images/openpay/cards2.png";

const { Text } = Typography;

const PurchaseSummaryCard = ({
  productsCount,
  subtotal,
  freeShipping,
  products,
  getProductPrice,
  destinationAddress,
  originAddress,
  onFetchAddress,
  onAddressUpdate,
}) => {
  const { isReady } = useContext(OpenPayContext);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [shippingCalculated, setShippingCalculated] = useState(false);
  const [shippingCost, setShippingCost] = useState(0);
  const [shippingInfo, setShippingInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showCardModal, setShowCardModal] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showAddressEditModal, setShowAddressEditModal] = useState(false);
  const [cardForm] = Form.useForm();
  const [cardError, setCardError] = useState("");
  const [currentDestinationAddress, setCurrentDestinationAddress] =
    useState(destinationAddress);
  const [addressEditTrigger, setAddressEditTrigger] = useState(0);

  const user = JSON.parse(localStorage.getItem("user"));
  
  const isAdmin = user?.role === "1" || user?.role === "admin" || user?.role === "administrator";
  const isClient = user?.role === "4" || user?.role === "client" || user?.role === "cliente";

  const total = subtotal + (shippingCalculated ? shippingCost : 0);
  const precioFinal = total;

  const handleShippingCalculated = (data) => {
    if (data.calculated && data.cost !== null) {
      setShippingCalculated(true);
      setShippingCost(data.cost);
      setShippingInfo(data);
      message.success(`Envío calculado: $${data.cost.toFixed(2)} MXN`);
    } else {
      setShippingCalculated(false);
      setShippingCost(0);
      setShippingInfo(null);
      if (data.error) {
        message.error(data.error);
      }
    }
  };

  const handleAddressLoaded = (address) => {
    setCurrentDestinationAddress(address);
  };

  const handleAddressEditSubmit = (newAddress) => {
    setCurrentDestinationAddress(newAddress);
    setShippingCalculated(false);
    setShippingCost(0);
    setShippingInfo(null);
    setAddressEditTrigger((prev) => prev + 1);
    if (onAddressUpdate) {
      onAddressUpdate(newAddress);
    }
    message.success("Dirección actualizada. Por favor recalcula el envío.");
  };

  const canProceedToPayment = () => {
    if (isAdmin) {
      return false;
    }
    const canProceed = acceptedTerms && shippingCalculated && productsCount > 0;
    return canProceed;
  };

  const generarToken = (cardData) =>
    new Promise((resolve, reject) => {
      if (!window.OpenPay?.token) {
        reject(new Error("OpenPay no está disponible"));
        return;
      }

      window.OpenPay.token.create(
        {
          card_number: cardData.cardNumber.replace(/\s/g, ""),
          holder_name: cardData.cardHolder,
          expiration_month: cardData.expiryMonth,
          expiration_year: cardData.expiryYear,
          cvv2: cardData.cvv,
        },
        (response) => {
          resolve(response.data.id);
        },
        (err) => {
          reject(new Error(err?.data?.description || "Error generando token"));
        }
      );
    });

  const formatCardNumber = (value) => {
    if (!value) return value;
    const v = value.replace(/\s+/g, "").replace(/[^0-9]/gi, "");
    const matches = v.match(/\d{4,16}/g);
    const match = (matches && matches[0]) || "";
    const parts = [];

    for (let i = 0, len = match.length; i < len; i += 4) {
      parts.push(match.substring(i, i + 4));
    }

    if (parts.length) {
      return parts.join(" ");
    } else {
      return value;
    }
  };

  const processPayment = async (tokenId) => {
    try {
      const cartProducts = products.map((p) => ({
        id: p.id,
        name: p.name,
        price: getProductPrice ? getProductPrice(p) : p.price,
        quantity: p.quantity || 1,
      }));

      const cartPayload = {
        client_id: user.id,
        products: cartProducts,
        change_type: "MXN",
        status: 1,
        shipping_info: shippingCalculated
          ? {
              cost: shippingCost,
              service: shippingInfo?.service,
              tracking_number: null,
              method: "dhl",
              weight: shippingInfo?.weight,
              quote_id: shippingInfo?.quoteId,
            }
          : null,
      };

      const cartRes = await fetch(
        `${BASE_URL}/${API.MARKETPLACE.CART.ADD_PRODUCT}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${
              user?.token || localStorage.getItem("token")
            }`,
          },
          body: JSON.stringify(cartPayload),
        }
      );

      const cartData = await cartRes.json();

      if (!cartData.success) {
        throw new Error(cartData.message || "Error creando carrito");
      }

      const payload = {
        cart_id: cartData.cart_id,
        client_id: user.id,
        token_id: tokenId,
        device_session_id: window.OpenPay.deviceData.setup(),
        redirect_url: `${window.location.origin}/#/marketplace/3d-secure-callback`,
        shipping_cost: shippingCost,
        shipping_service: shippingInfo?.service,
        total_amount: total,
        shipping_weight: shippingInfo?.weight,
      };

      const response = await fetch(
        `${BASE_URL}/${API.MARKETPLACE.CART.CHECKOUT_3D_SECURE}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${
              user?.token || localStorage.getItem("token")
            }`,
          },
          body: JSON.stringify(payload),
        }
      );

      const result = await response.json();

      if (!result.success || !result.payment_url) {
        throw new Error(result.message || "Error en pago");
      }

      localStorage.setItem("pending_cart_id", cartData.cart_id);
      localStorage.setItem("pending_cart_total", total.toString());
      localStorage.setItem(
        "shipping_info",
        JSON.stringify({
          cost: shippingCost,
          service: shippingInfo?.service,
          weight: shippingInfo?.weight,
        })
      );

      window.location.href = result.payment_url;
    } catch (error) {
      Modal.error({
        title: "Error en pago",
        content: error.message,
      });
      setLoading(false);
      setShowCardModal(false);
    }
  };

  const handleCardFormSubmit = async (values) => {
    if (!isReady) {
      setCardError(
        "Sistema de pagos no está listo. Por favor recarga la página."
      );
      return;
    }

    setLoading(true);
    setCardError("");

    try {
      if (!values.expiryMonth || !values.expiryYear) {
        throw new Error("La fecha de expiración es requerida");
      }

      const tokenId = await generarToken({
        cardNumber: values.cardNumber,
        cardHolder: values.cardHolder,
        expiryMonth: values.expiryMonth,
        expiryYear: values.expiryYear,
        cvv: values.cvv,
      });

      await processPayment(tokenId);
    } catch (error) {
      let errorMessage = error.message;

      if (errorMessage.includes("invalid card")) {
        errorMessage = "El número de tarjeta no es válido.";
      } else if (errorMessage.includes("expired")) {
        errorMessage = "La tarjeta está expirada o la fecha es incorrecta.";
      } else if (errorMessage.includes("cvv")) {
        errorMessage = "El código CVV es incorrecto.";
      } else if (errorMessage.includes("holder")) {
        errorMessage = "El nombre del titular es requerido.";
      }

      setCardError(errorMessage);
      setLoading(false);
    }
  };

  const openPaymentModal = () => {
    if (!isReady) {
      Modal.error({ title: "Error", content: "Sistema de pagos no listo" });
      return;
    }
    if (!acceptedTerms) {
      Modal.warning({
        title: "Términos y Condiciones",
        content: "Debes aceptar los términos y condiciones antes de continuar.",
      });
      return;
    }
    if (!shippingCalculated && !isAdmin) {
      Modal.warning({
        title: "Costo de Envío no calculado",
        content:
          "Debes calcular el costo de envío antes de proceder con el pago.",
      });
      return;
    }
    if (isAdmin) {
      Modal.warning({
        title: "Acción no permitida",
        content:
          "Los administradores no pueden realizar compras. Esta funcionalidad es solo para clientes.",
      });
      return;
    }
    if (!user?.id) {
      Modal.error({ title: "Error", content: "Usuario no identificado" });
      return;
    }
    if (productsCount === 0) {
      Modal.warning({ title: "Error", content: "Carrito vacío" });
      return;
    }

    setShowCardModal(true);
    setCardError("");
    cardForm.resetFields();
  };

  const handleAcceptTerms = () => {
    setAcceptedTerms(true);
    setShowTermsModal(false);
  };

  const handleViewTerms = () => {
    setShowTermsModal(true);
  };

  const cvvPopoverContent = (
    <div style={{ textAlign: "center", padding: "10px" }}>
      <img
        src={cvvPng}
        alt="Ejemplo de CVV"
        style={{
          width: "180px",
          height: "auto",
          marginBottom: "10px",
          borderRadius: "4px",
        }}
      />
      <p
        style={{
          margin: 0,
          fontSize: "13px",
          color: "#495057",
          fontWeight: "500",
        }}
      >
        El CVV son los 3 dígitos en el reverso de tu tarjeta
      </p>
    </div>
  );

  const renderShippingSection = () => {
    if (isAdmin) {
      return (
        <>
          <div className="summary-row shipping-calculator-row">
            <span className="summary-label">
              <TruckOutlined style={{ marginRight: 8 }} />
              Envío DHL:
            </span>
            <div className="shipping-calculator-container">
              <Alert
                message="Cotización de envío no disponible"
                description="Los administradores no pueden cotizar envíos. Esta funcionalidad está disponible solo para clientes registrados."
                type="info"
                showIcon
                icon={<WarningOutlined />}
                style={{ borderRadius: "8px" }}
              />
            </div>
          </div>
        </>
      );
    }

    if (freeShipping) {
      return (
        <div className="summary-row">
          <span className="summary-label">
            <CheckOutlined style={{ marginRight: 8 }} />
            Envío:
          </span>
          <span className="summary-value free-shipping-text">
            <CheckOutlined style={{ marginRight: 4 }} />
            Gratis (Ciudad Juárez)
          </span>
        </div>
      );
    }

    return (
      <>
        <div className="summary-row shipping-calculator-row">
          <span className="summary-label">
            <TruckOutlined style={{ marginRight: 8 }} />
            Envío DHL:
          </span>
          <div className="shipping-calculator-container">
            <DHLShippingCalculator
              key={addressEditTrigger}
              products={products}
              originAddress={originAddress}
              destinationAddress={currentDestinationAddress}
              onShippingCalculated={handleShippingCalculated}
              onFetchAddress={onFetchAddress}
              onAddressLoaded={handleAddressLoaded}
              isRequired={true}
            />
          </div>
        </div>

        {shippingCalculated && (
          <div className="summary-row">
            <span className="summary-label">
              <DollarOutlined style={{ marginRight: 8 }} />
              Costo de Envío:
            </span>
            <span className="summary-value">
              ${shippingCost.toFixed(2)} MXN
              {shippingInfo?.service && (
                <span
                  style={{ fontSize: "11px", color: "#999", marginLeft: "8px" }}
                >
                  ({shippingInfo.service})
                </span>
              )}
            </span>
          </div>
        )}
      </>
    );
  };

  const renderAddressInfo = () => {
    if (isAdmin) {
      return null;
    }
    
    if (!currentDestinationAddress) return null;

    return (
      <div className="address-info-section">
        <Divider className="address-divider" />
        <div className="address-info">
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <h4
              style={{
                fontSize: "14px",
                fontWeight: "600",
                marginBottom: "8px",
              }}
            >
              <TruckOutlined style={{ marginRight: "8px" }} />
              Dirección de envío:
            </h4>
            <Tooltip title="¿Quieres enviarlo a otra dirección?">
              <Button
                type="text"
                size="small"
                icon={<EditOutlined />}
                onClick={() => setShowAddressEditModal(true)}
                style={{ color: "#1890ff" }}
              >
                Editar
              </Button>
            </Tooltip>
          </div>
          <p style={{ fontSize: "13px", color: "#666", margin: 0 }}>
            {currentDestinationAddress.fullAddress ||
              currentDestinationAddress.addressLine ||
              "Dirección no registrada"}
            <br />
            {currentDestinationAddress.city &&
              `${currentDestinationAddress.city}, `}
            {currentDestinationAddress.state || ""}
            <br />
            CP: {currentDestinationAddress.zipCode || "No especificado"}
          </p>
        </div>
      </div>
    );
  };

  const renderPaymentModal = () => {
    return (
      <Modal
        title={null}
        open={showCardModal}
        onCancel={() => {
          if (!loading) {
            setShowCardModal(false);
          }
        }}
        footer={null}
        width={950}
        centered
        closable={!loading}
        maskClosable={false}
        className="custom-payment-modal"
        styles={{
          body: {
            padding: 0,
            margin: 0,
          },
        }}
      >
        <div style={{ padding: "35px 40px" }}>
          <div
            style={{
              marginBottom: "30px",
              padding: "15px 20px",
              backgroundColor: "#f8f9fa",
              border: "1px solid #e9ecef",
              borderRadius: "6px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <span
              style={{ fontSize: "16px", fontWeight: "600", color: "#495057" }}
            >
              Total a pagar:
            </span>
            <span
              style={{ fontSize: "24px", fontWeight: "bold", color: "#28a745" }}
            >
              ${total.toFixed(2)} MXN
            </span>
          </div>

          <h2
            style={{
              fontSize: "20px",
              margin: "0 0 25px 0",
              color: "#212529",
              fontWeight: "500",
              borderBottom: "1px solid #dee2e6",
              paddingBottom: "15px",
            }}
          >
            Tarjeta de crédito o débito
          </h2>

          <div
            style={{
              marginBottom: "30px",
              display: "flex",
              gap: "20px",
              flexWrap: "wrap",
            }}
          >
            <div
              style={{
                flex: 1,
                backgroundColor: "#f8f9fa",
                borderRadius: "8px",
                padding: "20px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginBottom: "15px",
                }}
              >
                <img
                  src={radioOnPng}
                  alt="radio"
                  style={{
                    width: "20px",
                    height: "20px",
                    marginRight: "10px",
                  }}
                />
                <h4
                  style={{
                    margin: 0,
                    fontSize: "16px",
                    fontWeight: "600",
                    color: "#212529",
                  }}
                >
                  Tarjetas de crédito
                </h4>
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  minHeight: "60px",
                }}
              >
                <img
                  src={cards1Png}
                  alt="tarjetas de crédito"
                  style={{
                    maxWidth: "100%",
                    height: "auto",
                    maxHeight: "50px",
                  }}
                />
              </div>
            </div>

            <div
              style={{
                flex: 1,
                backgroundColor: "#f8f9fa",
                borderRadius: "8px",
                padding: "20px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginBottom: "15px",
                }}
              >
                <img
                  src={radioOnPng}
                  alt="radio"
                  style={{
                    width: "20px",
                    height: "20px",
                    marginRight: "10px",
                  }}
                />
                <h4
                  style={{
                    margin: 0,
                    fontSize: "16px",
                    fontWeight: "600",
                    color: "#212529",
                  }}
                >
                  Tarjetas de débito
                </h4>
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  minHeight: "60px",
                }}
              >
                <img
                  src={cards2Png}
                  alt="tarjetas de débito"
                  style={{
                    maxWidth: "100%",
                    height: "auto",
                    maxHeight: "50px",
                  }}
                />
              </div>
            </div>
          </div>

          {cardError && (
            <div
              style={{
                marginBottom: "25px",
                padding: "12px 16px",
                backgroundColor: "#f8d7da",
                border: "1px solid #f5c6cb",
                borderRadius: "6px",
                color: "#721c24",
                fontSize: "14px",
              }}
            >
              {cardError}
            </div>
          )}

          <Form
            form={cardForm}
            layout="vertical"
            onFinish={handleCardFormSubmit}
            style={{ width: "100%" }}
          >
            <Form.Item name="token_id" hidden>
              <Input />
            </Form.Item>
            <Form.Item name="use_card_points" hidden initialValue="false">
              <Input />
            </Form.Item>

            <Form.Item
              label={
                <span
                  style={{
                    fontSize: "14px",
                    fontWeight: "500",
                    color: "#495057",
                  }}
                >
                  Nombre del titular
                </span>
              }
              name="cardHolder"
              rules={[
                {
                  required: true,
                  message: "Ingresa el nombre como aparece en la tarjeta",
                },
                { min: 3, message: "Mínimo 3 caracteres" },
              ]}
            >
              <Input
                placeholder="Como aparece en la tarjeta"
                autoComplete="off"
                data-openpay-card="holder_name"
                disabled={loading}
                style={{
                  width: "100%",
                  padding: "10px 14px",
                  border: "1px solid #ced4da",
                  borderRadius: "6px",
                  fontSize: "15px",
                  textTransform: "uppercase",
                }}
              />
            </Form.Item>

            <Form.Item
              label={
                <span
                  style={{
                    fontSize: "14px",
                    fontWeight: "500",
                    color: "#495057",
                  }}
                >
                  Número de tarjeta
                </span>
              }
              name="cardNumber"
              rules={[
                { required: true, message: "Ingresa el número de tarjeta" },
                {
                  pattern: /^[\d\s]{16,19}$/,
                  message: "Número de tarjeta inválido",
                },
              ]}
            >
              <Input
                placeholder="1234 5678 9012 3456"
                autoComplete="off"
                data-openpay-card="card_number"
                maxLength={19}
                disabled={loading}
                onChange={(e) => {
                  const formatted = formatCardNumber(e.target.value);
                  cardForm.setFieldsValue({ cardNumber: formatted });
                }}
                style={{
                  width: "100%",
                  padding: "10px 14px",
                  border: "1px solid #ced4da",
                  borderRadius: "6px",
                  fontSize: "15px",
                }}
              />
            </Form.Item>

            <Row gutter={20}>
              <Col span={14}>
                <Form.Item
                  label={
                    <span
                      style={{
                        fontSize: "14px",
                        fontWeight: "500",
                        color: "#495057",
                      }}
                    >
                      Fecha de expiración
                    </span>
                  }
                  required
                  style={{ marginBottom: "20px" }}
                >
                  <Row gutter={12}>
                    <Col span={12}>
                      <Form.Item
                        name="expiryMonth"
                        noStyle
                        rules={[
                          { required: true, message: "Mes requerido" },
                          {
                            pattern: /^(0[1-9]|1[0-2])$/,
                            message: "Mes inválido (01-12)",
                          },
                        ]}
                      >
                        <Input
                          placeholder="Mes"
                          maxLength={2}
                          autoComplete="off"
                          data-openpay-card="expiration_month"
                          disabled={loading}
                          style={{
                            width: "100%",
                            padding: "10px 14px",
                            border: "1px solid #ced4da",
                            borderRadius: "6px",
                            fontSize: "15px",
                          }}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        name="expiryYear"
                        noStyle
                        rules={[
                          { required: true, message: "Año requerido" },
                          {
                            pattern: /^[0-9]{2}$/,
                            message: "Año inválido (últimos 2 dígitos)",
                          },
                        ]}
                      >
                        <Input
                          placeholder="Año"
                          maxLength={2}
                          autoComplete="off"
                          data-openpay-card="expiration_year"
                          disabled={loading}
                          style={{
                            width: "100%",
                            padding: "10px 14px",
                            border: "1px solid #ced4da",
                            borderRadius: "6px",
                            fontSize: "15px",
                          }}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                </Form.Item>
              </Col>

              <Col span={10}>
                <Form.Item
                  label={
                    <span
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <span
                        style={{
                          fontSize: "14px",
                          fontWeight: "500",
                          color: "#495057",
                        }}
                      >
                        Código de seguridad
                      </span>
                      <Popover
                        content={cvvPopoverContent}
                        title="¿Qué es el CVV?"
                        trigger="click"
                        placement="top"
                      >
                        <QuestionCircleOutlined
                          style={{
                            color: "#6c757d",
                            fontSize: "16px",
                            cursor: "pointer",
                          }}
                        />
                      </Popover>
                    </span>
                  }
                  required
                  style={{ marginBottom: "20px" }}
                >
                  <Row gutter={12} align="middle">
                    <Col span={16}>
                      <Form.Item
                        name="cvv"
                        noStyle
                        rules={[
                          { required: true, message: "Ingresa el CVV" },
                          {
                            pattern: /^\d{3,4}$/,
                            message: "CVV inválido (3-4 dígitos)",
                          },
                        ]}
                      >
                        <Input
                          placeholder="3 dígitos"
                          maxLength={4}
                          type="password"
                          autoComplete="off"
                          data-openpay-card="cvv2"
                          disabled={loading}
                          style={{
                            width: "100%",
                            padding: "10px 14px",
                            border: "1px solid #ced4da",
                            borderRadius: "6px",
                            fontSize: "15px",
                          }}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Popover
                        content={cvvPopoverContent}
                        title="¿Qué es el CVV?"
                        trigger="hover"
                        placement="right"
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer",
                          }}
                        >
                          <img
                            src={cvvPng}
                            alt="cvv"
                            style={{
                              width: "100%",
                              maxWidth: "70px",
                              height: "auto",
                              display: "block",
                              border: "1px solid #e9ecef",
                              borderRadius: "4px",
                              padding: "2px",
                            }}
                          />
                        </div>
                      </Popover>
                    </Col>
                  </Row>
                </Form.Item>
              </Col>
            </Row>

            <div
              style={{
                marginBottom: "30px",
                padding: "20px",
                backgroundColor: "#f8f9fa",
                borderRadius: "8px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: "15px",
              }}
            >
              <div
                style={{ display: "flex", alignItems: "center", gap: "10px" }}
              >
                <span style={{ fontSize: "14px", color: "#6c757d" }}>
                  Transacciones realizadas vía:
                </span>
                <img
                  src={openpayLogoPng}
                  alt="OpenPay"
                  style={{
                    height: "26px",
                    width: "auto",
                  }}
                />
              </div>
              <div
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                <img
                  src={securityPng}
                  alt="security"
                  style={{
                    width: "18px",
                    height: "18px",
                  }}
                />
                <span style={{ fontSize: "12px", color: "#868e96" }}>
                  Tus pagos se realizan de forma segura con encriptación de 256
                  bits
                </span>
              </div>
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "15px",
              }}
            >
              <Button
                type="default"
                onClick={() => setShowCardModal(false)}
                style={{
                  padding: "10px 30px",
                  height: "46px",
                  fontSize: "16px",
                  borderRadius: "6px",
                }}
                disabled={loading}
              >
                Cancelar
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                id="pay-button"
                loading={loading}
                disabled={!isReady || loading || isAdmin}
                style={{
                  padding: "10px 40px",
                  height: "46px",
                  fontSize: "16px",
                  fontWeight: "500",
                  backgroundColor: "#007bff",
                  borderColor: "#007bff",
                  borderRadius: "6px",
                }}
              >
                {loading ? "Procesando pago..." : "Pagar"}
              </Button>
            </div>
          </Form>
        </div>
      </Modal>
    );
  };

  return (
    <>
      <AddressEditModal
        visible={showAddressEditModal}
        onClose={() => setShowAddressEditModal(false)}
        onAddressSubmit={handleAddressEditSubmit}
        currentAddress={currentDestinationAddress}
      />

      <TermsAndConditionsModal
        open={showTermsModal}
        onAccept={handleAcceptTerms}
        onCancel={() => setShowTermsModal(false)}
      />

      {renderPaymentModal()}

      <Card className="purchase-summary-card">
        <div className="summary-title">
          <h3>
            <ShoppingOutlined style={{ marginRight: 8 }} />
            Resumen de Compra
          </h3>
        </div>

        <div className="summary-content">
          <div className="summary-row">
            <span className="summary-label">
              <ShoppingCartOutlined style={{ marginRight: 8 }} />
              Productos:
            </span>
            <span className="summary-value">{productsCount}</span>
          </div>

          <div className="summary-row">
            <span className="summary-label">
              <DollarOutlined style={{ marginRight: 8 }} />
              Subtotal:
            </span>
            <span className="summary-value">${subtotal.toFixed(2)}</span>
          </div>

          {renderShippingSection()}

          {renderAddressInfo()}

          <Divider className="summary-divider" />

          <div className="summary-total">
            <span className="total-label">Total:</span>
            <span className="total-value">${total.toFixed(2)}</span>
          </div>

          <div
            className="summary-row"
            style={{ marginTop: 8, marginBottom: 16 }}
          >
            <span className="summary-label" style={{ fontWeight: 600 }}>
              <SafetyOutlined style={{ marginRight: 8, color: "#52c41a" }} />
              Precio final (IVA incluido):
            </span>
            <span
              className="summary-value"
              style={{ fontWeight: 700, color: "#28a745", fontSize: "18px" }}
            >
              ${precioFinal.toFixed(2)}
            </span>
          </div>

          <Divider className="terms-divider" />

          <div className="terms-section">
            <div className="terms-checkbox">
              <Checkbox
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
                className="terms-checkbox-input"
                disabled={!shippingCalculated || isAdmin}
              >
                <span className="terms-text">
                  Acepto los
                  <Button
                    type="link"
                    onClick={handleViewTerms}
                    className="terms-link"
                    style={{ marginLeft: 4, padding: 0, height: "auto" }}
                  >
                    Términos y Condiciones
                  </Button>
                </span>
              </Checkbox>
              {!shippingCalculated && !isAdmin && (
                <div
                  style={{ marginTop: 8, fontSize: "12px", color: "#ff4d4f" }}
                >
                  <Tooltip title="Primero debes calcular el costo de envío">
                    <span>
                      Debes calcular el envío antes de aceptar términos
                    </span>
                  </Tooltip>
                </div>
              )}
              {isAdmin && (
                <div
                  style={{ marginTop: 8, fontSize: "12px", color: "#ff4d4f" }}
                >
                  <Tooltip title="Los administradores no pueden realizar compras">
                    <span>
                      Los administradores no pueden aceptar términos ni realizar compras
                    </span>
                  </Tooltip>
                </div>
              )}
              <div style={{ marginTop: 8 }}>
                <Button
                  type="link"
                  size="small"
                  onClick={handleViewTerms}
                  icon={<FileTextOutlined />}
                  style={{ padding: 0, fontSize: "12px" }}
                >
                  Leer completo
                </Button>
              </div>
            </div>

            {acceptedTerms && !shippingCalculated && !isAdmin && (
              <Alert
                message="Importante"
                description="Debes calcular el costo de envío antes de proceder con el pago."
                type="warning"
                showIcon
                className="terms-alert"
                style={{ marginTop: 12 }}
              />
            )}

            {!acceptedTerms && shippingCalculated && !isAdmin && (
              <Alert
                message="Importante"
                description="Debes aceptar los términos y condiciones para proceder con el pago."
                type="warning"
                showIcon
                className="terms-alert"
                style={{ marginTop: 12 }}
              />
            )}

            {isAdmin && (
              <Alert
                message="Acción no permitida"
                description="Los administradores no pueden realizar compras. Esta funcionalidad está disponible solo para clientes registrados."
                type="error"
                showIcon
                className="terms-alert"
                style={{ marginTop: 12 }}
              />
            )}
          </div>

          <Tooltip
            title={
              isAdmin
                ? "Los administradores no pueden realizar compras"
                : !shippingCalculated
                ? "Calcula el costo de envío antes de continuar"
                : !acceptedTerms
                ? "Acepta los términos y condiciones para continuar"
                : ""
            }
            placement="top"
          >
            <Button
              type="primary"
              block
              icon={<CreditCardOutlined />}
              disabled={
                !canProceedToPayment() ||
                loading ||
                !isReady ||
                productsCount === 0 ||
                isAdmin
              }
              loading={loading}
              onClick={openPaymentModal}
              className="purchase-button"
              size="large"
            >
              {loading ? "Procesando..." : "Continuar con el Pago"}
            </Button>
          </Tooltip>

          <div
            style={{
              marginTop: 16,
              textAlign: "center",
              padding: "12px",
              backgroundColor: "#f6ffed",
              borderRadius: "6px",
              border: "1px solid #b7eb8f",
            }}
          >
            <SafetyOutlined
              style={{
                color: "#52c41a",
                marginRight: 8,
                fontSize: "16px",
              }}
            />
            <Text
              type="secondary"
              style={{
                fontSize: "12px",
                color: "#52c41a",
              }}
            >
              Pago 100% seguro • Datos protegidos
            </Text>
          </div>
        </div>
      </Card>
    </>
  );
};

export default PurchaseSummaryCard;